import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ACTIVE_GREEN, ACTIVE_STATUS_CHIP } from '../../constants/statusColors';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  AccountTree as AccountTreeIcon,
  CorporateFare as ParentCompanyIcon,
  Domain as SubsidiaryIcon,
  Download as DownloadIcon,
  CloudUploadOutlined as ImportIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import StatCard from '../../components/common/StatCard';
import FilterPanel from '../../components/common/FilterPanel';
import DataTable from '../../components/common/DataTable';
import CompanyImportReviewModal from '../../components/companies/CompanyImportReviewModal';
import { useAuth } from '../../context/AuthContext';
import { canAccessCompaniesList, isCompanySecretaryRole } from '../../constants/csAdminRoles';
import { useGlobalPermission } from '../../hooks/useGlobalPermission';
import { COMPANY_STATUS } from '../../data/companiesData';
import { csAdminApi } from '../../services/api';
import { mapCompanyFromApi } from '../../utils/apiMappers';
import { useLookups } from '../../hooks/useLookups';
import {
  downloadCompanyTemplate,
  parseImportedCompanyRow,
  validateImportedCompanyRow,
  calculateCompanySimilarityScore,
  buildCompanyCreatePayload,
  isEmptyCompanyRow,
  isCompanyTemplateHelperRow,
  getUserAssignedCompanyIds,
} from '../../utils/companyImportTemplate';

const CompaniesList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importedCompanies, setImportedCompanies] = useState([]);
  const [importing, setImporting] = useState(false);
  const isCompanySecretary = isCompanySecretaryRole(user?.role);
  const { allowed: canCreateCompanies, loading: permissionLoading } = useGlobalPermission('companies', 'create');
  const showCreateActions = canCreateCompanies && !permissionLoading;
  const assignedCompanyIds = getUserAssignedCompanyIds(user);
  const [summary, setSummary] = useState({
    total: 0,
    active: 0,
    subsidiaries: 0,
  });

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [structureFilter, setStructureFilter] = useState('');
  const [entityClassificationFilter, setEntityClassificationFilter] = useState('');

  const { items: companyTypeLookups } = useLookups('COMPANY_TYPE');
  const { items: entityClassificationLookups } = useLookups('ENTITY_CLASSIFICATION');
  const { items: countryLookups } = useLookups('COUNTRY');

  // Group-scope (IT Admin, Secretarial Admin) and company secretaries
  useEffect(() => {
    if (!canAccessCompaniesList(user?.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.state?.refresh]);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter, typeFilter, structureFilter, entityClassificationFilter, companies]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);

      const res = await csAdminApi.getCompanies();
      const companyList = (res.data?.data || []).map((row) => {
        const c = mapCompanyFromApi(row);
        return {
          ...c,
          tradeLicenseNumber: c.tradeLicenseNumber || '',
          assignedCS: c.assignedCS || '—',
          companyType: c.governanceTypeName || c.type,
          governanceTypeCode: c.governanceTypeCode,
          structureType: c.structureType,
        };
      });
      setCompanies(companyList);
      setSummary({
        total: companyList.length,
        active: companyList.filter((c) => c.status === 'ACTIVE').length,
        subsidiaries: companyList.filter((c) => c.structureType === 'Subsidiary').length,
      });
      setError('');
    } catch (err) {
      console.error('Error fetching companies:', err);
      const status = err.response?.status;
      if (status === 401) {
        setError('Session expired or invalid. Please log out and sign in again.');
      } else {
        const isNetworkError = !err.response;
        setError(
          err.response?.data?.error?.message ||
            (isNetworkError
              ? 'Failed to load companies. Check that the API is reachable (same host /api/v1 on dev).'
              : 'Failed to load companies.')
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...companies];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (company) =>
          company.name.toLowerCase().includes(query) ||
          company.tradeLicenseNumber.toLowerCase().includes(query) ||
          (company.assignedCS && company.assignedCS.toLowerCase().includes(query))
      );
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter((company) => company.status === statusFilter);
    }

    // Type filter
    if (typeFilter) {
      filtered = filtered.filter((company) => company.governanceTypeCode === typeFilter);
    }

    // Structure filter
    if (structureFilter) {
      filtered = filtered.filter((company) => company.structureType === structureFilter);
    }

    // Entity Classification filter
    if (entityClassificationFilter) {
      filtered = filtered.filter((company) => company.entityClassification === entityClassificationFilter);
    }

    setFilteredCompanies(filtered);
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
  };

  const handleFilterChange = (filterName, value) => {
    if (filterName === 'status') {
      setStatusFilter(value);
    } else if (filterName === 'type') {
      setTypeFilter(value);
    } else if (filterName === 'structure') {
      setStructureFilter(value);
    } else if (filterName === 'entityClassification') {
      setEntityClassificationFilter(value);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setTypeFilter('');
    setStructureFilter('');
    setEntityClassificationFilter('');
  };

  // Stat card click handlers
  const handleTotalCompaniesClick = () => {
    handleClearFilters();
  };

  const handleActiveCompaniesClick = () => {
    handleClearFilters();
    setStatusFilter('ACTIVE');
  };

  const handleSubsidiariesClick = () => {
    handleClearFilters();
    setStructureFilter('Subsidiary');
  };

  const handleDownloadTemplate = () => {
    downloadCompanyTemplate();
    enqueueSnackbar('Template downloaded', { variant: 'success' });
  };

  const findMatchingCompanies = (importedCompany, existingCompanies) => {
    return existingCompanies
      .map((existing) => ({
        ...existing,
        score: calculateCompanySimilarityScore(importedCompany, existing),
      }))
      .filter((match) => match.score >= 50)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
  };

  const performImport = async (companiesToImport) => {

    try {
      setImporting(true);
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      for (const company of companiesToImport) {
        try {
          if (company.resolution?.action === 'skip'
            || company.resolution?.action?.startsWith('map-')) {
            continue;
          }

          if (!company.resolution && company.status === 'duplicate') {
            continue;
          }

          const response = await csAdminApi.createCompany(buildCompanyCreatePayload(company));

          if (response.status !== 201 && response.status !== 200) {
            throw new Error(response.data?.error?.message || response.data?.message || 'Create failed');
          }
          if (response.data?.success === false) {
            throw new Error(response.data?.error?.message || response.data?.message || 'Create failed');
          }

          successCount += 1;
        } catch (err) {
          console.error(`Error importing company ${company.name}:`, err);
          const errorMsg = err.response?.data?.error?.message
            || err.response?.data?.message
            || err.message
            || 'Unknown error';
          errors.push(`${company.name}: ${errorMsg}`);
          errorCount += 1;
        }
      }

      if (successCount > 0) {
        enqueueSnackbar(`Successfully imported ${successCount} company(ies)`, { variant: 'success' });
        fetchCompanies();
      }
      if (errorCount > 0) {
        enqueueSnackbar(`Failed to import ${errorCount} company(ies). Check console for details.`, { variant: 'error' });
        errors.forEach((entry) => console.error('  -', entry));
      }

      setImportModalOpen(false);
      setImportedCompanies([]);
    } catch (err) {
      console.error('Error performing import:', err);
      enqueueSnackbar(`Error during import: ${err.message || 'Unknown error'}`, { variant: 'error' });
    } finally {
      setImporting(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setImporting(true);
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(firstSheet);

          if (rows.length === 0) {
            enqueueSnackbar('No data found in the file', { variant: 'warning' });
            setImporting(false);
            return;
          }

          let companiesMap = {};
          let existingCompanies = [];
          {
            try {
              const res = await csAdminApi.getCompanies();
              existingCompanies = (res.data?.data || []).map(mapCompanyFromApi);
              existingCompanies.forEach((company) => {
                const nameLower = company.name.toLowerCase().trim();
                companiesMap[nameLower] = company.id;
              });
            } catch (err) {
              console.error('Error loading companies for import:', err);
              enqueueSnackbar('Failed to load companies. Please try again.', { variant: 'error' });
              setImporting(false);
              return;
            }
          }

          const processedCompanies = [];
          for (let i = 0; i < rows.length; i++) {
            const parsed = parseImportedCompanyRow(rows[i], i);
            if (isEmptyCompanyRow(parsed) || isCompanyTemplateHelperRow(parsed)) {
              continue;
            }

            let parentCompanyId = null;
            let parentLookupError = null;

            if (parsed.structureType === 'Subsidiary') {
              if (!parsed.parentCompanyName) {
                parentLookupError = 'Parent Company Name is required for subsidiaries';
              } else {
                const parentNameLower = parsed.parentCompanyName.toLowerCase().trim();
                parentCompanyId = companiesMap[parentNameLower];
                if (!parentCompanyId) {
                  const suggestions = Object.keys(companiesMap).filter((name) =>
                    name.includes(parentNameLower) || parentNameLower.includes(name));
                  const suggestion = suggestions.length > 0
                    ? ` Did you mean: ${suggestions.join(', ')}?`
                    : '';
                  parentLookupError = `Parent company "${parsed.parentCompanyName}" not found.${suggestion} Parent must already exist before importing subsidiaries.`;
                }
              }
            }

            parsed.parentCompanyId = parentCompanyId;
            parsed.parentLookupError = parentLookupError;

            const validation = validateImportedCompanyRow(parsed, {
              companyTypes: companyTypeLookups,
              countries: countryLookups,
              entityClassifications: entityClassificationLookups,
              isCompanySecretary,
              assignedCompanyIds,
            });

            parsed.governanceTypeCode = validation.governanceTypeCode || parsed.governanceTypeCode;
            parsed.governanceTypeName = validation.governanceTypeName || parsed.governanceTypeCode;

            let status = 'ready';
            let errors = [];
            let matches = [];

            if (!validation.isValid) {
              status = 'error';
              errors = validation.errors;
            } else if (parentLookupError) {
              status = 'error';
              errors = [parentLookupError];
            } else {
              matches = findMatchingCompanies(parsed, existingCompanies);
              if (matches.length > 0) {
                status = 'duplicate';
              }
            }

            processedCompanies.push({
              ...parsed,
              status,
              errors,
              matches,
            });
          }

          if (processedCompanies.length === 0) {
            enqueueSnackbar('No company rows found in the file', { variant: 'warning' });
            setImporting(false);
            return;
          }

          const hasIssues = processedCompanies.some((c) => c.status !== 'ready');
          if (hasIssues) {
            setImportedCompanies(processedCompanies);
            setImportModalOpen(true);
          } else {
            await performImport(processedCompanies);
          }
        } catch (err) {
          console.error('Error processing file:', err);
          enqueueSnackbar('Error processing file. Please check the format.', { variant: 'error' });
        } finally {
          setImporting(false);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (err) {
      console.error('Error reading file:', err);
      enqueueSnackbar('Error reading file', { variant: 'error' });
      setImporting(false);
    }

    event.target.value = '';
  };

  const columns = [
    {
      id: 'name',
      label: 'Company Name',
      minWidth: 280,
      render: (row) => {
        const isParent = row.structureType === 'Parent';
        const color = isParent ? '#2196F3' : '#9C27B0';
        const icon = isParent ? <ParentCompanyIcon sx={{ fontSize: 20 }} /> : <SubsidiaryIcon sx={{ fontSize: 20 }} />;
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '8px',
                bgcolor: `${color}15`,
                color: color,
              }}
            >
              {icon}
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {row.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {row.tradeLicenseNumber} {row.country && row.country !== 'UAE' && `• ${row.country}`}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      id: 'type',
      label: 'Classification',
      minWidth: 160,
      render: (row) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Chip label={row.companyType || row.governanceTypeCode} size="small" color="secondary" variant="outlined" />
          <Typography variant="caption" color="textSecondary">
            {row.structureType || '—'}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'assignedCS',
      label: 'Company Secretary',
      minWidth: 180,
      render: (row) => row.assignedCS || <em>Not assigned</em>,
    },
    {
      id: 'setupProgress',
      label: 'Setup Progress',
      minWidth: 140,
      render: (row) => {
        const progress = Math.max(0, Math.min(100, Number(row.setupProgress) || 0));
        return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 60,
              height: 6,
              bgcolor: '#E0E0E0',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: `${progress}%`,
                height: '100%',
                bgcolor: progress === 100 ? ACTIVE_GREEN : progress > 0 ? '#FFC107' : '#BDBDBD',
              }}
            />
          </Box>
          <Typography variant="caption">{progress}%</Typography>
        </Box>
        );
      },
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 120,
      render: (row) => {
        const statusConfig = {
          ACTIVE: ACTIVE_STATUS_CHIP,
          INACTIVE: { bg: '#F5F5F5', color: '#757575', label: 'NEUTRAL' },
          PENDING: { bg: '#FFF3E0', color: '#E65100', label: 'WARNING' },
        };
        const config = statusConfig[row.status] || statusConfig.INACTIVE;
        
        return (
          <Chip
            label={row.status}
            sx={{
              bgcolor: config.bg,
              color: config.color,
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 24,
              borderRadius: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
            size="small"
          />
        );
      },
    },
  ];

  const filterOptions = [
    {
      label: 'Classification',
      name: 'type',
      value: typeFilter,
      options: [
        { value: '', label: 'All Classifications' },
        ...companyTypeLookups.map((t) => ({ value: t.code, label: t.label })),
      ],
    },
    {
      label: 'Structure',
      name: 'structure',
      value: structureFilter,
      options: [
        { value: '', label: 'All Structures' },
        { value: 'Parent', label: 'Parent / Group' },
        { value: 'Subsidiary', label: 'Subsidiary' },
      ],
    },
    {
      label: 'Entity (Classification)',
      name: 'entityClassification',
      value: entityClassificationFilter,
      options: [
        { value: '', label: 'All Entity Classifications' },
        ...entityClassificationLookups.map((t) => ({ value: t.code, label: t.label })),
      ],
    },
    {
      label: 'Status',
      name: 'status',
      value: statusFilter,
      options: [
        { value: '', label: 'All Status' },
        { value: COMPANY_STATUS.ACTIVE, label: 'Active' },
        { value: COMPANY_STATUS.INACTIVE, label: 'Inactive' },
        { value: COMPANY_STATUS.PENDING, label: 'Pending' },
      ],
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {user?.role === 'COMPANY_SECRETARY' ? 'My Companies' : 'Company Information'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {user?.role === 'COMPANY_SECRETARY' 
              ? 'Companies assigned to you for management'
              : 'Manage company entities and their organizational structure'
            }
          </Typography>
        </Box>
        {showCreateActions && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
            >
              Download Template
            </Button>
            <Button
              variant="outlined"
              component="label"
              startIcon={importing ? <CircularProgress size={20} /> : <ImportIcon />}
              disabled={importing}
            >
              Import Companies
              <input
                type="file"
                hidden
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
              />
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/companies/create')}
            >
              {isCompanySecretary ? 'Add Subsidiary' : 'Create Company'}
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <StatCard 
            title="Total Companies" 
            value={summary.total} 
            icon={BusinessIcon} 
            color="#2196F3" 
            onClick={handleTotalCompaniesClick}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard 
            title="Active Companies" 
            value={summary.active} 
            icon={CheckCircleIcon} 
            color={ACTIVE_GREEN}
            onClick={handleActiveCompaniesClick}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard 
            title="Subsidiaries" 
            value={summary.subsidiaries} 
            icon={AccountTreeIcon} 
            color="#FF9800"
            onClick={handleSubsidiariesClick}
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <FilterPanel
        searchValue={searchQuery}
        onSearchChange={handleSearch}
        searchPlaceholder="Search by company name, license number, or CS name..."
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
      />

      {/* Companies Table */}
      <DataTable
        rows={filteredCompanies}
        columns={columns}
        onRowClick={(row) => navigate(`/companies/${row.id}`)}
        emptyMessage="No companies found"
      />

      {importedCompanies.length > 0 && (
        <CompanyImportReviewModal
          open={importModalOpen}
          onClose={() => {
            setImportModalOpen(false);
            setImportedCompanies([]);
          }}
          importedCompanies={importedCompanies}
          onImport={performImport}
        />
      )}
    </Container>
  );
};

export default CompaniesList;
